import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlarmClock, CalendarCheck, Lock, Sun } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { useDragSensors } from '@/lib/dndSensors';
import { useTodos, useUpdateTodo } from '@/features/todos/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { DueChip, TaskRow, type TaskRowTimer } from '@/components/shared/TaskRow';
import { useUsers } from '@/features/settings/api';
import { useTaskRowTimer } from '@/features/time/useTaskRowTimer';
import { openSinceLabel, useAttendanceChip } from '@/features/attendance';
import type { TaskView } from '@/features/tasks/api';
import { useMyDay, useSetMyDayOrder } from './api';

function Section({
  icon,
  title,
  count,
  tone,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  tone?: 'danger' | 'warn';
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <h2
        className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium uppercase tracking-wide"
        style={{
          color:
            tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--text-muted)',
        }}
      >
        {icon}
        {title}
        <span className="vt-mono">{count}</span>
      </h2>
      {children}
    </section>
  );
}

function SortableRow({
  task,
  people,
  timer,
  onOpen,
}: {
  task: TaskView;
  people: Record<string, { name: string; avatarUrl?: string }>;
  timer: TaskRowTimer;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: task._id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      className="cursor-grab active:cursor-grabbing"
    >
      <TaskRow task={task} people={people} timer={timer} onOpen={onOpen} />
    </div>
  );
}

export function MyDayPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useMyDay();
  const { data: people = [] } = useUsers();
  const setOrder = useSetMyDayOrder();
  const { data: todos = [] } = useTodos('open');
  const updateTodo = useUpdateTodo();
  const timerFor = useTaskRowTimer();
  const { data: chip } = useAttendanceChip();

  const [nextUp, setNextUp] = useState<TaskView[]>([]);
  useEffect(() => setNextUp(data?.nextUp ?? []), [data?.nextUp]);

  const peopleById = useMemo(
    () => Object.fromEntries(people.map((p) => [p._id, { name: p.name, avatarUrl: p.avatarUrl }])),
    [people],
  );

  // V10 §7 — shared sensors; see `lib/dndSensors.ts`.
  const sensors = useDragSensors({ pointerDistance: 4 });

  /** Reordering here is personal focus order and never touches board rank. */
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = nextUp.findIndex((t) => t._id === active.id);
    const newIndex = nextUp.findIndex((t) => t._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(nextUp, oldIndex, newIndex);
    setNextUp(reordered);
    setOrder.mutate(reordered.map((t) => t._id));
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="My Day" />
        <div className="flex flex-col gap-2 p-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9" />
          ))}
        </div>
      </>
    );
  }

  // Due today or already past — the same rule as the sidebar badge.
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const personalDue = todos.filter((t) => t.dueDate && new Date(t.dueDate) <= endOfToday);

  const overdue = data?.overdue ?? [];
  const today = data?.today ?? [];
  const total = overdue.length + today.length + nextUp.length;

  return (
    <>
      <PageHeader
        title="My Day"
        subtitle={
          total === 0
            ? 'Nothing assigned — a good day to pick up something from the backlog.'
            : `${overdue.length} overdue · ${today.length} due today · ${nextUp.length} next up`
        }
      />

      {/* V6-04 §4 — the same chip state, said in words, where the day starts. */}
      {chip?.enabled && (chip.open || chip.firstIn) && (
        <p className="px-4 pt-3 text-[12px] vt-muted" data-testid="my-day-attendance">
          {chip.open
            ? `You started at ${new Date(chip.open.startAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} ${
                // V10 §1 — a session from an earlier day must not read as "today".
                openSinceLabel(chip.open.workDate, chip.today) ?? ''
              } — still working.`.replace(/ {2,}/g, ' ')
            : `You started at ${new Date(chip.firstIn!).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} and have ended for today.`}
        </p>
      )}

      <div className="max-w-[900px] p-4">
        {total === 0 ? (
          <EmptyState
            icon={<Sun className="size-6" />}
            title="Your day is clear"
            description="Nothing is assigned to you and overdue or due today."
            action={
              <Button variant="secondary" onClick={() => navigate('/my-tasks')}>
                See all my tasks
              </Button>
            }
          />
        ) : (
          <>
            {overdue.length > 0 && (
              <Section
                icon={<AlarmClock className="size-3.5" />}
                title="Overdue"
                count={overdue.length}
                tone="danger"
              >
                <div className="vt-surface overflow-hidden">
                  {overdue.map((task) => (
                    <TaskRow
                      key={task._id}
                      task={task}
                      people={peopleById}
                      onOpen={() => navigate(`/tasks/${task.key}`)}
                    />
                  ))}
                </div>
              </Section>
            )}

            {today.length > 0 && (
              <Section
                icon={<CalendarCheck className="size-3.5" />}
                title="Due today"
                count={today.length}
                tone="warn"
              >
                <div className="vt-surface overflow-hidden">
                  {today.map((task) => (
                    <TaskRow
                      key={task._id}
                      task={task}
                      people={peopleById}
                      onOpen={() => navigate(`/tasks/${task.key}`)}
                    />
                  ))}
                </div>
              </Section>
            )}

            {nextUp.length > 0 && (
              <Section icon={<Sun className="size-3.5" />} title="Next up" count={nextUp.length}>
                <p className="mb-1.5 text-[11px] vt-muted">
                  Drag to set your own focus order — it is yours alone and never changes the board.
                </p>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={nextUp.map((t) => t._id)} strategy={verticalListSortingStrategy}>
                    <div className="vt-surface overflow-hidden">
                      {nextUp.map((task) => (
                        <SortableRow
                          key={task._id}
                          task={task}
                          people={peopleById}
                          timer={timerFor(task._id)}
                          onOpen={() => navigate(`/tasks/${task.key}`)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </Section>
            )}

            {/* V5-04 §3 — personal to-dos that are due today or overdue. The
                task sections above are untouched: these are a different kind of
                thing and are not mixed in with project work. */}
            {personalDue.length > 0 && (
              <Section
                icon={<Lock className="size-3.5" />}
                title="Personal"
                count={personalDue.length}
              >
                <p className="mb-1.5 text-[11px] vt-muted">Only you can see these.</p>
                <div className="vt-surface overflow-hidden" data-testid="my-day-personal">
                  {personalDue.map((todo) => (
                    <label
                      key={todo._id}
                      className="flex items-center gap-2 border-b border-[var(--border)] px-2 py-1.5 last:border-0"
                      data-testid="my-day-todo"
                      data-title={todo.title}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Complete ${todo.title}`}
                        checked={false}
                        onChange={() => updateTodo.mutate({ id: todo._id, status: 'done' })}
                      />
                      <span className="min-w-0 flex-1 truncate text-[13px]">{todo.title}</span>
                      <DueChip dueDate={todo.dueDate} />
                    </label>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </>
  );
}
